import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { alertsFromError, homeLocationNote, lateLabel, type CheckoutKind, type HomeLocationResult } from '../lib/checkouts';
import { addDays, CONDITION_LABEL, dueDateInputToIso, formatDate, formatDateTime, toDateInput } from '../lib/format';
import { endOfSchoolYear, invalidateInventory } from '../lib/hooks';
import type { AssetCondition, Checkout } from '../lib/types';
import { alertGate, BorrowerAlertsPanel, useBorrowerAlerts } from './borrower-alerts';
import { CheckinExtrasFields, useCheckinExtras } from './checkin-extras';
import { ReportIncidentModal } from './device-history';
import { AssigneePicker, assigneeToInput, HomeLocationQuestion, useHomeLocationAnswer, type AssigneeValue } from './pickers';
import { useToast } from './toast';
import { Alert, Badge, Button, Field, Input, Modal, Select, Textarea } from './ui';

const CONDITIONS = Object.keys(CONDITION_LABEL) as AssetCondition[];

export interface CheckoutAsset {
  id: string;
  assetTag: string;
  name: string;
  category?: { name: string } | null;
}

export function CheckOutDialog({
  open,
  onClose,
  assets,
  initialAssignee,
  onDone,
}: {
  open: boolean;
  onClose(): void;
  assets: CheckoutAsset[];
  initialAssignee?: AssigneeValue;
  onDone?(checkouts: Checkout[]): void;
}) {
  const { me, can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const defaultDays = me?.tenant?.defaultCheckoutDays ?? 14;

  const [assignee, setAssignee] = useState<AssigneeValue>(initialAssignee ?? { type: 'PERSON', person: null });
  const [dueDate, setDueDate] = useState('');
  const [condition, setCondition] = useState<AssetCondition | ''>('');
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const home = useHomeLocationAnswer(assignee);

  useEffect(() => {
    if (!open) return;
    home.reset();
    setAssignee(initialAssignee ?? { type: 'PERSON', person: null });
    setDueDate(toDateInput(addDays(defaultDays)));
    setCondition('');
    setNotes('');
    setConfirmed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Alerts on the person borrowing it, before it goes out.
  const personId = assignee.type === 'PERSON' ? assignee.person?.id : undefined;
  const alerts = useBorrowerAlerts(personId, { kind: 'STANDARD', enabled: open });
  useEffect(() => setConfirmed(false), [personId]);
  const gate = alertGate(personId ? alerts.data : [], confirmed, can('ADMIN'));

  const input = assigneeToInput(assignee);
  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ checkouts: Checkout[]; home: HomeLocationResult | null }>('/checkouts', {
        assetIds: assets.map((asset) => asset.id),
        assignee: input,
        dueAt: dueDateInputToIso(dueDate),
        condition: condition || undefined,
        notes: notes || undefined,
        overrideAlerts: gate.override,
        ...home.body(),
      }),
    onSuccess: ({ checkouts, home: moved }) => {
      invalidateInventory(queryClient);
      const label = checkouts[0]?.assigneeLabel ?? 'assignee';
      toast.success(
        `${assets.length === 1 ? `Checked out ${assets[0].assetTag} to ${label}` : `Checked out ${assets.length} assets to ${label}`}${homeLocationNote(moved)}`,
      );
      onDone?.(checkouts);
      onClose();
    },
    onError: (err) => {
      if (alertsFromError(err)) void alerts.refetch();
      toast.error(errorMessage(err));
    },
  });

  const quickDates = [
    { label: 'Tomorrow', value: toDateInput(addDays(1)) },
    { label: '1 week', value: toDateInput(addDays(7)) },
    { label: `${defaultDays} days`, value: toDateInput(addDays(defaultDays)) },
    { label: 'End of school year', value: toDateInput(endOfSchoolYear()) },
    { label: 'No due date', value: '' },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={assets.length === 1 ? `Check out ${assets[0].assetTag}` : `Check out ${assets.length} assets`}
      description={assets.length === 1 ? assets[0].name : undefined}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!input || !home.ready || (!!personId && (alerts.isLoading || !gate.ok))}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Check out
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {assets.length > 1 && (
          <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
            {assets.map((asset) => (
              <Badge key={asset.id} tone="brand">
                {asset.assetTag}
              </Badge>
            ))}
          </div>
        )}
        <Field as="div" label="Assign to" required>
          <AssigneePicker inline value={assignee} onChange={setAssignee} />
        </Field>
        {home.room && <HomeLocationQuestion room={home.room} value={home.value} onChange={home.set} />}
        <BorrowerAlertsPanel alerts={personId ? alerts.data : undefined} loading={!!personId && alerts.isLoading} confirmed={confirmed} onConfirm={setConfirmed} />
        <Field label="Due date" hint={dueDate ? `Due ${formatDate(dueDateInputToIso(dueDate))}` : 'No due date - long-term assignment'}>
          <Input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {quickDates.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => setDueDate(option.value)}
                className={
                  dueDate === option.value
                    ? 'rounded-full bg-brand-700 px-2.5 py-1 text-xs font-medium text-white'
                    : 'rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200'
                }
              >
                {option.label}
              </button>
            ))}
          </div>
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Condition">
            <Select value={condition} onChange={(event) => setCondition(event.target.value as AssetCondition | '')}>
              <option value="">Keep current condition</option>
              {CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {CONDITION_LABEL[c]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Notes">
          <Textarea value={notes} maxLength={2000} onChange={(event) => setNotes(event.target.value)} placeholder="Accessories included, reason for loan…" />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Checks in a device: its condition, the school's check-in form, and photos. A device that comes back damaged is offered an
 * incident report afterwards, so the damage stays in its history.
 */
export function CheckInDialog({
  open,
  onClose,
  checkout,
  onDone,
}: {
  open: boolean;
  onClose(): void;
  checkout: Pick<Checkout, 'id' | 'assigneeLabel' | 'dueAt'> & {
    personId?: string | null;
    kind?: CheckoutKind;
    asset: { id?: string; assetTag: string; name: string };
  };
  onDone?(): void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [condition, setCondition] = useState<AssetCondition | ''>('');
  const [assetStatus, setAssetStatus] = useState('AVAILABLE');
  const [notes, setNotes] = useState('');
  const [stage, setStage] = useState<'form' | 'damaged' | 'report'>('form');
  const extras = useCheckinExtras(open);
  const alerts = useBorrowerAlerts(checkout.personId, { checkoutId: checkout.id, enabled: open });

  useEffect(() => {
    if (!open) return;
    setCondition('');
    setAssetStatus('AVAILABLE');
    setNotes('');
    setStage('form');
    extras.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const lateMs = checkout.dueAt ? Date.now() - new Date(checkout.dueAt).getTime() : 0;
  const late = lateMs > 0 ? Math.floor(lateMs / 86_400_000) : null;
  const asset = checkout.asset;

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/checkouts/${checkout.id}/checkin`, { condition: condition || undefined, notes: notes || undefined, assetStatus, ...extras.body() }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(`Checked in ${asset.assetTag}`);
      onDone?.();
      if (condition === 'DAMAGED' && asset.id && can('TECHNICIAN')) setStage('damaged');
      else onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <>
      <Modal
        open={open && stage !== 'report'}
        onClose={onClose}
        size={stage === 'form' ? 'md' : 'sm'}
        title={stage === 'form' ? `Check in ${checkout.kind === 'LOANER' ? 'loaner ' : ''}${asset.assetTag}` : `${asset.assetTag} came back damaged`}
        description={stage === 'form' ? `${asset.name} · from ${checkout.assigneeLabel}` : undefined}
        footer={
          stage === 'form' ? (
            <>
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="primary" disabled={!extras.ready} loading={mutation.isPending} onClick={() => mutation.mutate()}>
                {extras.uploading ? 'Uploading photos…' : 'Check in'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose}>Not now</Button>
              <Button variant="primary" icon={<ShieldAlert />} onClick={() => setStage('report')}>
                Report an incident
              </Button>
            </>
          )
        }
      >
        {stage === 'form' ? (
          <div className="space-y-4">
            {late !== null && (
              <Alert tone="amber" title={lateLabel(late).replace('Returned late', 'Coming back late')}>
                It was due {formatDateTime(checkout.dueAt)}.
              </Alert>
            )}
            <BorrowerAlertsPanel alerts={alerts.data} mode="checkin" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Returned condition">
                <Select value={condition} onChange={(event) => setCondition(event.target.value as AssetCondition | '')}>
                  <option value="">Not inspected</option>
                  {CONDITIONS.map((c) => (
                    <option key={c} value={c}>
                      {CONDITION_LABEL[c]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="After check-in the asset is">
                <Select value={assetStatus} onChange={(event) => setAssetStatus(event.target.value)}>
                  <option value="AVAILABLE">Available to check out</option>
                  <option value="IN_REPAIR">In repair</option>
                  <option value="LOST">Lost (not returned)</option>
                  <option value="STOLEN">Stolen</option>
                  <option value="RETIRED">Retired</option>
                </Select>
              </Field>
            </div>
            <Field label="Notes">
              <Textarea value={notes} maxLength={2000} onChange={(event) => setNotes(event.target.value)} placeholder="Damage, missing charger…" />
            </Field>
            <CheckinExtrasFields extras={extras} />
            {extras.missing.length > 0 && <p className="text-xs text-slate-500">Answer {extras.missing.join(', ')} to check it in.</p>}
          </div>
        ) : (
          <p className="text-sm text-slate-600">Report an incident to keep the damage in the device’s repair history, with its photos, and bill for it if needed.</p>
        )}
      </Modal>
      {asset.id && (
        <ReportIncidentModal open={open && stage === 'report'} onClose={onClose} asset={{ id: asset.id, assetTag: asset.assetTag, name: asset.name }} stay />
      )}
    </>
  );
}

export function ChangeDueDateDialog({
  open,
  onClose,
  checkout,
}: {
  open: boolean;
  onClose(): void;
  checkout: Pick<Checkout, 'id' | 'dueAt' | 'assigneeLabel'> & { asset: { assetTag: string } };
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (open) setDueDate(toDateInput(checkout.dueAt));
  }, [open, checkout.dueAt]);

  const mutation = useMutation({
    mutationFn: () => api.patch(`/checkouts/${checkout.id}`, { dueAt: dueDateInputToIso(dueDate) }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success('Due date updated');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={`Change due date`}
      description={`${checkout.asset.assetTag} · ${checkout.assigneeLabel}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <Field label="Due date" hint="Clear the date for a long-term assignment. Reminders start over for the new date.">
        <Input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
      </Field>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {[1, 7, 14, 30].map((days) => (
          <Button key={days} size="sm" variant="ghost" onClick={() => setDueDate(toDateInput(addDays(days)))}>
            +{days} day{days === 1 ? '' : 's'}
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => setDueDate(toDateInput(endOfSchoolYear()))}>
          End of school year
        </Button>
      </div>
    </Modal>
  );
}
